import { memo } from 'react';
import { Tooltip } from 'antd';
import { ReactSVG } from 'react-svg';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import SheetContextMenu from '../contextMenu/SheetContextMenu';
import { StyledSheetCell, ShiftChangeSymbol, StandbyIcon, WarningSymbol, BadgeSymbol } from './Cell.style';
import swapIcon from '../../../../assets/images/icons/awesome-exchange-alt.svg';
import standbyIcon from '../../../../assets/images/icons/clock-regular.svg';
import warningIcon from '../../../../assets/images/icons/triangle-exclamation-solid.svg';
import userIcon from '../../../../assets/images/icons/user-alt.svg';
import { useAuthStateValue } from '../../../../context/AuthContext';
import { VALIDATION_RULES } from '../../../../util/monthlyScheduleValidationConfig';

const Cell = memo((props) => {
  const {
    date,
    // rowIndex,
    numberOfDays,
    sheetData,
    cellValidation,
    demand,
    cellValueInOriginalData,
    cellValueInModifiedData,
    previousMonthValidation,
    nextMonthValidation,
    latestChangeValue,
    controllerId,
    controllerFullName,
    isShiftManager,
    isSatco,
    isSuperIntendent,
    multiselect,
    isSelected,
    isPartOfMultiselect,
    isDisabled,
    isMatterOfShiftChangeRequest,
    wasMatterOfShiftChangeRequest,
    loggedUserHasEditPermissions,
    modifiedData,
    setModifiedData,
    moveHighlightBars,
    mainTableRef,
    myShift,
    elseShift,
    nextCellFinalValue
  } = props;

  const { t } = useTranslation();
  const { loggedUser } = useAuthStateValue();
  const { location, mode } = useParams();

  const locationId = loggedUser?.locationsData.find((el) => el?.value === location)?.id;
  const loggedUserLocation = loggedUser?.extendUser?.locationIdentificator;
  const ATCU = (mode === 'shiftsChange' || !mode) ? loggedUserLocation : location;

  function isWithinThreeDays() {
    const d1 = new Date();
    const d2 = new Date(date);
    if (isNaN(d1) || isNaN(d2)) {
      return false;
    }
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
    const diffInMs = d2 - d1;
    const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;

    return diffInMs >= 0 && diffInMs <= threeDaysInMs;
  }

  const cellFinalValue = cellValueInModifiedData ? cellValueInModifiedData?.newState : (cellValueInOriginalData ? cellValueInOriginalData : null);
  const cellFinalAbsence = loggedUser?.absenceTypes?.find((el) => el?.id === cellFinalValue?.absenceId);
  const cellFinalShift = loggedUser?.shiftTypes?.[ATCU]?.find((el) => el?.id === cellFinalValue?.shiftId);
  // const nextCellFinalShift = loggedUser?.[ATCU]?.shiftTypes?.find((el) => el?.id === nextCellFinalValue?.shiftId);
  const nextCellFinalAbsence = loggedUser?.absenceTypes?.find((el) => el?.id === nextCellFinalValue?.absenceId);
  const shiftType = cellFinalValue?.shiftId ? loggedUser?.shiftTypes?.[ATCU]?.find((el) => el?.id === cellFinalValue?.shiftId) : null;
  const absenceType = (cellFinalValue?.absenceId || cellFinalValue?.absenceId === 0) ? loggedUser?.absenceTypes?.find((el) => el?.id === cellFinalValue?.absenceId) : null;
  const cellWarnings = {
    exceededMaxWorkingDaysInARow:
      (cellFinalShift?.name || cellFinalAbsence?.name === 'SP')
      && (cellValidation.workDaysStreak === VALIDATION_RULES.MAX_WORKING_DAYS_IN_CYCLE)
      && (cellValidation.restDaysStreak === 0),
    belowMinimumRecreational:
        cellFinalAbsence?.name?.startsWith('R')
        && (cellValidation?.recreationalVacationStreak < 14)
        && !nextCellFinalAbsence?.name?.startsWith('R')
        && date.getDate() !== numberOfDays,
    aboveMaximumRecreational:
        cellFinalAbsence?.name?.startsWith('R')
        && (cellValidation?.recreationalVacationStreak > 14),
    belowMinimumRecreationalAtEndOfMonth:
        cellFinalAbsence?.name?.startsWith('R')
        && date.getDate() === numberOfDays && ((nextMonthValidation?.recreationalVacationStreak + cellValidation?.recreationalVacationStreak) < 14),
    vacationCombinedWithRecreationalVacation:
        (cellFinalAbsence?.name?.startsWith('R')
        && cellValidation?.vacationRestStreak === 0
        && cellValidation?.recreationalVacationStreak === 0)
        || (
            cellFinalAbsence?.name?.startsWith('G')
            && cellValidation?.recreationalVacationStreak > 0
        ),
    breakingTheRestAfterNightShift:
      (cellFinalValue?.shiftId || cellFinalValue?.absenceName === 'SP')
      && ((cellValidation?.previousCellWasNightShift)
        || (cellValidation?.nightShiftRestDaysStreak > 0 && cellValidation?.nightShiftRestDaysStreak < VALIDATION_RULES.REST_DAYS_AFTER_NIGHT_SHIFT)
      ),
    breakingTheRestAfterNoonShift:
      shiftType?.name?.startsWith('J')
      && cellValidation.previousCellWasNoonShift,
    breakingTheNoAbsenceAfterNightShiftRule:
      (cellValidation?.previousCellWasNightShift)
      && (!!cellFinalValue?.absenceId)
      && (absenceType?.name !== 'BO')
      && (absenceType?.name !== 'B'),
    breakingTheRestAfterNightShiftFromPrevMonth: (!!cellFinalValue?.shiftId || cellFinalValue?.absenceName === 'SP')
      && (
          (date.getDate() === 1 && (previousMonthValidation?.lastCellShiftName === 'N' || previousMonthValidation?.penultCellShiftName === 'N'))
          || (date.getDate() === 2 && (previousMonthValidation?.lastCellShiftName === 'N'))
          ),
    firstDaysInNextMonthShouldBeRestingAfterMaxWorkingDaysInARow:
      ((!!cellFinalValue?.shiftId || cellFinalValue?.absenceName === 'SP') && date.getDate() === numberOfDays)
      && (cellValidation?.workDaysStreak === (VALIDATION_RULES.MAX_WORKING_DAYS_IN_CYCLE - 1))
      && (!!nextMonthValidation?.firstCellShiftId || nextMonthValidation?.firstCellAbsenceName === 'SP'),
    isNightShiftAtMonthEndWithoutEnoughRestInFirstDaysOfNextMonth:
      (shiftType?.name === 'N')
      && ((date.getDate() === numberOfDays && (!!nextMonthValidation?.firstCellShiftId || !!nextMonthValidation?.secondCellShiftId || nextMonthValidation?.firstCellAbsenceName === 'SP' || nextMonthValidation?.secondCellAbsenceName === 'SP'))
        || ((date.getDate() === (numberOfDays - 1)) && (!!nextMonthValidation?.firstCellShiftId || nextMonthValidation?.firstCellAbsenceName === 'SP'))
      ),
    isNoonShiftAtMonthEndWithoutEnoughRestInFirstDaysOfNextMonth:
      (date?.getDate() === numberOfDays && shiftType?.name?.startsWith('P'))
      && (nextMonthValidation?.firstCellShiftName?.startsWith('J'))
  };

  const renderWarnings = () => {
    const warnings = []
    Object.entries(cellWarnings).forEach((el) => {
      if (el[1] === true) {
        warnings.push(<li>{t('monthlyScheduleWarnings.' + el[0])}</li>);
      }
    });
    return warnings?.length > 0 ? (<ul>{warnings}</ul>) : null;
    // let debugString = '';
    // // eslint-disable-next-line no-restricted-syntax
    // for (const [key, value] of Object.entries(cellValidation)) {
    //   debugString = debugString + '\n -' + key + ': ' + value;
    // }
    // return debugString;
  }

  const cellHasWarnings = !!Object.entries(cellWarnings)?.find((el) => el[1] === true);

  return (
    <SheetContextMenu
      enabled={(mode === 'edit' && loggedUser?.localPermissions?.[locationId]?.monthlyPlan !== 'MODIFY_NEXT_N_DAYS') || (mode === 'edit' && loggedUser?.localPermissions?.[locationId]?.monthlyPlan === 'MODIFY_NEXT_N_DAYS' && isWithinThreeDays())}
      date={date}
      key={'main-cell' + controllerFullName + date}
      sheetData={sheetData}
      isWeekend={date.getDay() === 6 || date.getDay() === 0}
      demand={demand}
      hasWarnings={cellHasWarnings}
      ATCU={ATCU}
      numberOfDays={numberOfDays}
      controllerId={controllerId}
      controllerFullName={controllerFullName}
      isPartOfMultiselect={isPartOfMultiselect}
      multiselect={multiselect}
      modifiedData={modifiedData}
      setModifiedData={setModifiedData}
      isShiftManager={isShiftManager}
      isSatco={isSatco}
      isSuperIntendent={isSuperIntendent}
      roleName={ATCU === 'ATCU1' ? 'SHIFT_MANAGER' : (ATCU === 'ATCU2' ? 'SATCO' : null)}
      mainTableRef={mainTableRef}
    >
      <Tooltip title={renderWarnings()}>
        <StyledSheetCell
          sheetMode={mode}
          isWeekend={date.getDay() === 6 || date.getDay() === 0}
          key={date.date}
          isDisabled={isDisabled}
          isSelected={isSelected}
          isModified={!!cellValueInModifiedData}
          alreadyMatterOfRequest={isMatterOfShiftChangeRequest}
          onContextMenu={isDisabled ? null : moveHighlightBars}
          className={'col-' + (date.getDate()) + ' '
              + (cellHasWarnings ? 'hasWarning' : '')
              + (((controllerId === myShift?.controllerId && date.getDate() === myShift?.day && (date.getMonth() + 1) === myShift.month) || (controllerId === elseShift?.controllerId && date.getDate() === elseShift?.day && (date.getMonth() + 1) === elseShift?.month)) ? 'isSelectedForShiftSwap' : '')
              + ((wasMatterOfShiftChangeRequest || isMatterOfShiftChangeRequest) || (mode !== 'shiftsChange' && loggedUserHasEditPermissions && (wasMatterOfShiftChangeRequest || isMatterOfShiftChangeRequest)) ? ' zCell' : '')}
          modifiedCell={!!latestChangeValue}
          type={shiftType ? 'SHIFT' : (absenceType ? 'ABSENCE' : 'NONE')}
          shiftType={shiftType}
          absenceType={absenceType}
          hasRole={cellFinalValue?.isShiftManager || cellFinalValue?.isSatco}
          role={cellFinalValue?.isShiftManager ? 'SHIFT_MANAGER' : (cellFinalValue?.isSatco ? 'SATCO' : 'NONE')}
          onClick={isDisabled ? null : moveHighlightBars}
        >
          {isMatterOfShiftChangeRequest && <ShiftChangeSymbol><ReactSVG src={swapIcon} /></ShiftChangeSymbol>}
          {absenceType?.name === 'SB' && <StandbyIcon><ReactSVG src={standbyIcon} /></StandbyIcon>}
          {/* {((wasMatterOfShiftChangeRequest || isMatterOfShiftChangeRequest) || (sheetMode !== 'shiftsChange' && loggedUserHasEditPermissions && (wasMatterOfShiftChangeRequest || isMatterOfShiftChangeRequest))) && <ZSymbol pending={isMatterOfShiftChangeRequest}><ReactSVG src={zIcon} /></ZSymbol>} */}
          {shiftType?.name ? shiftType?.name : (absenceType ? absenceType.name : '')}
          {(absenceType?.name !== 'SB' && cellHasWarnings) && <WarningSymbol><ReactSVG src={warningIcon} /></WarningSymbol>}
          {(cellFinalValue?.isShiftManager || cellFinalValue?.isSatco) && (<BadgeSymbol><ReactSVG src={userIcon} /></BadgeSymbol>)}
        </StyledSheetCell>
      </Tooltip>
    </SheetContextMenu>
  );
}, (prevProps, newProps) => {
  return (
    (prevProps?.cellValueInModifiedData === newProps?.cellValueInModifiedData)
    && (prevProps?.isPartOfMultiselect === newProps?.isPartOfMultiselect)
    && (prevProps?.isSelected === newProps?.isSelected)
    && (JSON.stringify(prevProps?.cellValidation) === JSON.stringify(newProps?.cellValidation))
    && (JSON.stringify(prevProps?.previousMonthValidation) === JSON.stringify(newProps?.previousMonthValidation))
    && (JSON.stringify(prevProps?.nextMonthValidation) === JSON.stringify(newProps?.nextMonthValidation))
    && (prevProps?.myShift === newProps?.myShift)
    && (prevProps?.elseShift === newProps?.elseShift)
    && (prevProps?.isMatterOfShiftChangeRequest === newProps?.isMatterOfShiftChangeRequest)
    && (prevProps?.sheetMode === newProps?.sheetMode)
    && (prevProps?.isDisabled === newProps?.isDisabled)
    && (prevProps?.wasMatterOfShiftChangeRequest === newProps?.wasMatterOfShiftChangeRequest)
    && (prevProps?.nextCellFinalValue === newProps?.nextCellFinalValue)
  )
});

export default Cell;
